/**
 * pages/registry.ts —— view → 页面模块 的【唯一注册表】
 *
 * 页面注册只在本文件维护；新增路由同时核对 parse / guards，步骤见模块地图。
 *
 * ⚠⚠ 骨架期（W0-4）除 login 外全是 placeholder。
 *   **W2 每接完一个模块，就把那一行换成真模块**；上线前这里【不许再剩 placeholder】。
 *   ✅ **2026-08-22（W2-8）起 P1 八个模块全部接完**；
 *   ✅ **2026-08-25 起 `bulletins` 也接完了**，本表**一个 placeholder 都不剩**。
 *   ⚠ `placeholder.ts` 仍然留着（本文件已不再 import 它）：它是加新模块时的起手式，不是死代码。
 *
 * ⚠⚠ 【2026-08-24】曾经有过一个例外条目 `assistant`（AI 助手的「功能建设中」空态页）。
 *   **它已整条退役**：AI 助手改成了运营总览 Hero 上的**右侧抽屉**（`shared/assistant.ts`），
 *   没有路由、没有骨架、不进本注册表。别按老注释把它加回来。
 *   决策留痕见 `03_归档/00_决策留痕.md` §二 26，方案见 `01_需求文档/15_AI接入方案.md` §四之一。
 */
import type { PageModule } from './types.js';
import { loginPage } from './login.js';
import { adminPage } from './admin.js';
import { teamsPage } from './teams.js';
import { teamDetailPage } from './team-detail.js';
import { checkinsPage } from './checkins.js';
import { okrPage } from './okr.js';
import { reportsPage } from './reports.js';
import { overviewPage } from './overview.js';
import { bulletinsPage } from './bulletins.js';

const PAGES: Record<string, PageModule<never>> = {
  /* 登录与自助改密，身份流程见 login.ts。 */
  login: loginPage as PageModule<never>,

  /* W2-8 ✅ Hero 四块 + 队伍运营矩阵（四列可排序）+ 最近活动 + 两个快捷入口
     🔴 **2026-08-20 删了四块**（需跟进条 / 矩阵工具栏整行 / 风险 KR 列 / 14天活动+参与人次两列），
        出参与界面都已瘦身——**不要"补回来"**，论证见 03_归档/13 §二之一。
     ⚠ 运营总览由此**不再有催办入口**（管理员确认不需要），催办只剩 Hero 副行与双周报页。
     ⚠ 这一页一个数都不自己算，连排序都在服务端（红线 4）。 */
  overview: overviewPage as PageModule<never>,
  /* W2-4 ✅ 列表页做全（新建 / 编辑 / 管理成员 / 归档 / 恢复）；
     详情页包含档案、成员、OKR、活动、双周报与时间线，均由 team-detail.ts 接口数据渲染。 */
  teams: teamsPage as PageModule<never>,
  detail: teamDetailPage as PageModule<never>,
  /* W2-5 ✅ 主从两栏全做（左栏队伍选择器 + 右栏 O/KR 树 + O/KR 增删改 + 进度快调）
     🔴 进度是**滑块 + 数字框联动，松手才提交**（2026-08-20 变更 ④），不是一期的「数字框 + 保存」；
        拖动过程中一个请求都不发——这条路径挂着乐观锁 version，边拖边发会让冲突频繁误报。
     ⚠ 本页**有只读态**：所有在用队伍全营可见，只有本队成员与管理员能改（一期没有这个态）。
     ⚠ 已归档队伍不在本页；它的 OKR 在队伍详情页里只读可查。 */
  okr: okrPage as PageModule<never>,
  /* W2-6 ✅ 三页式全做（列表 → 详情 → 编辑），含补交与 KR 同步
     🔴 **提交单向不可逆**：没有撤回 / 重新提交 / 管理员确认 / 退回——不是漏做，是产品上就不存在。
     🔴 **历史期补交不同步 KR**：编辑页不渲染 KR 区，且**服务端会拒绝携带 krSync 的历史期提交**
        （同步语义是「把 KR 当前值改成拟更新值」，对已结束的期同步会造成进度倒退）。
     ⚠ **二期不保存草稿**（草稿归三期）：草稿只活在 state.rp.draft 里，
       所以编辑页有**离开拦截**（`shared/events.ts` 的 setLeaveGuard）——这是必做项，不是可选项。
     ⚠ 版本冲突弹窗**直接 import OKR 页那一个**，不要写第二个。 */
  reports: reportsPage as PageModule<never>,
  /* W2-7 ✅ 列表 + 录入 / 编辑 / 详情 / 删除四个弹窗全做（2026-08-22，提到 W2-5 之前做）
     ⚠ 照片走 COS 直传，**bucket 未开通期间是 mock 模式**：不真的上传，只在当次页面预览。
       弹窗里那行说明必须留着，否则用户会以为刷新后图没了是 bug。
     ⚠ 首页「一键打卡」入口随 W2-8 运营总览一起挂，本模块只提供弹窗本体。 */
  checkins: checkinsPage as PageModule<never>,
  /* ✅ 2026-08-25 接完（`01_需求文档/16_项目快报方案.md`）。**它不再是 P2 占位**。
     🔴 三条与一期最大的差别，看到旧写法不要照抄：
        ① **纯管理员功能**（队长 / 队员没有入口，服务端 requireAdmin）；
        ② 周期由管理员**显式选择**（hash 带期号），单队快报**已砍掉**；
        ③ 快报**落库**，历史记录按周期分组，重新生成产生新记录、旧的保留。
     ⚠ 纸那一块是**红线 1 的例外**（海报主视觉），例外范围只限 `.bp` 子树，
       **不许把那套颜色扩散到任何别的页面**。 */
  bulletins: bulletinsPage as PageModule<never>,
  /* 管理后台只有周期、人员两个 Tab；系统与数据 Tab 随 Excel 导出取消。 */
  admin: adminPage as PageModule<never>,

};

export function pageOf(view: string): PageModule<never> {
  const p = PAGES[view];
  // 走到这里说明 guards 放行了一个没注册的 view —— 两边加页面时漏了一处，早报早好
  if (!p) throw new Error(`[registry] 未注册的页面：${view}（在 pages/registry.ts 里补一行）`);
  return p;
}
